import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";
import { styles } from "../../styles/pages/SubCategory/SubCategoryEditStyles";
import Loader from "../../components/common/Loader";
import ListboxComponent from "../../components/common/ListboxComponent";

const SubCategoryEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [subcatName, setSubcatName] = useState("");
  const [subcatDesc, setSubcatDesc] = useState("");
  const [proCatId, setProCatId] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState<{ subcat_name?: string; pro_cat_id?: string }>({});
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [categories, setCategories] = useState<{ id: number; category_name: string }[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const token = sessionStorage.getItem("access_token");
  const userId = sessionStorage.getItem("user_id");

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        setFetchError("No authentication token found. Please log in.");
        setLoading(false);
        return;
      }
      if (!id) {
        setFetchError("No subcategory ID provided.");
        setLoading(false);
        return;
      }

      try {
        const formData = new FormData();
        formData.append("sub_cat_id", id);
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/subcategory/edit`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        if (response.data.status === 200 && response.data.data && response.data.data.length > 0) {
          const { subcat_name, subcat_desc, category_id } = response.data.data[0];
          setSubcatName(subcat_name || "");
          setSubcatDesc(subcat_desc || "");
          // Use category_id if available, otherwise set to an empty string
          setProCatId(category_id ? String(category_id) : "");
        } else {
          setFetchError(`Failed to load subcategory data. Status: ${response.data.status}, Message: ${response.data.message || 'No data'}`);
        }

        const catResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/category/name/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (catResponse.data.status === 200) {
          setCategories(catResponse.data.data);
        } else {
          setFetchError(`Failed to fetch category list. Status: ${catResponse.data.status}`);
        }
      } catch (error: any) {
        console.error("Fetch error details:", error.response?.data || error.message);
        setFetchError(`Error fetching data: ${error.response?.data?.message || error.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, id, navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    setFetchError(null);

    if (!token || !id) {
      setErrors({ subcat_name: "Authorization or subcategory ID required. Please log in." });
      setSaving(false);
      return;
    }

    const newErrors: { subcat_name?: string; pro_cat_id?: string } = {};

    if (!subcatName.trim()) {
      newErrors.subcat_name = "The Subcategory name field is required";
    }
    if (!proCatId) {
      newErrors.pro_cat_id = "Please select a category";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSaving(false);
      return;
    }

    const formData = new FormData();
    formData.append("subcat_name", subcatName);
    formData.append("subcat_desc", subcatDesc || "");
    formData.append("user_id", userId || "");
    formData.append("pro_cat_id", proCatId);
    formData.append("sub_cat_id", id);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/subcategory/update`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setTimeout(() => {
          setSuccessModalOpen(false);
          navigate("/subcategory-list");
        }, 2000);
        return;
      }
      if (response.data.status === 422 && response.data.message) {
        setErrors(
          Object.fromEntries(
            Object.entries(response.data.message).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ])
          )
        );
        return;
      }
      if (response.data.status === 500) {
        setErrors({
          subcat_name: response.data.message || "Internal Server Error",
        });
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "An unexpected error occurred.";
      setErrors({
        subcat_name: errorMessage,
      });
      if (error.response?.status === 422 && error.response.data.errors) {
        setErrors(
          Object.fromEntries(
            Object.entries(error.response.data.errors).map(([key, value]) => [
              key,
              Array.isArray(value) ? value[0] : value,
            ])
          )
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "subcat_name") {
      setSubcatName(value);
      if (!value.trim()) {
        setErrors({ ...errors, subcat_name: "The Subcategory name field is required" });
      } else {
        setErrors({ ...errors, subcat_name: undefined });
      }
    } else if (name === "subcat_desc") {
      setSubcatDesc(value);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Edit Sub Category</h2>
      </div>
      {loading && <Loader />}
      {!loading && (
        <div className={styles.formContainer}>
          {fetchError && <div className={styles.errorText}>{fetchError}</div>}
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputContainer}>
              <div>
                <label className={styles.label}>
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subcat_name"
                  value={subcatName}
                  onChange={handleInputChange}
                  className={`${styles.input} ${errors.subcat_name
                    ? styles.inputError
                    : "border-gray-300 focus:border-brand-300 focus:ring-3 focus:ring-brand-500/10"
                    }`}
                />
                {errors.subcat_name && (
                  <span className={styles.errorText}>{errors.subcat_name}</span>
                )}
              </div>
              <ListboxComponent
                value={categories.find((cat) => cat.id === Number(proCatId)) ?? null}
                onChange={(cat) => {
                  const value = cat ? String(cat.id) : "";
                  setProCatId(value);
                  setErrors((prev) => ({
                    ...prev,
                    pro_cat_id: value ? undefined : "Please select a category",
                  }));
                }}
                items={categories}
                label="Category"
                displayKey="category_name"
                error={errors.pro_cat_id}
                required
              />

              <div>
                <label className={styles.label}>Description</label>
                <textarea
                  name="subcat_desc"
                  value={subcatDesc}
                  onChange={handleInputChange}
                  className={`${styles.textareaField} h-24`}
                  style={{ textAlign: "justify" }}
                />
              </div>
            </div>

            <div className={styles.buttonContainer}>
              <button
                type="submit"
                disabled={saving}
                className={styles.submitButton}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                type="button" // Changed from type="submit" to type="button"
                onClick={() => navigate("/subcategory-list")}
                className={styles.cancelButton}
              >
                Cancel
              </button>
            </div>
          </form>

          <Modal
            isOpen={isSuccessModalOpen}
            onClose={() => setSuccessModalOpen(false)}
            className="w-[80%] max-w-[400px] bg-white dark:bg-gray-900 px-6 py-6 rounded-lg shadow-xl mx-auto text-center relative"
          >
            <div className="flex justify-center">
              <div className="w-14 h-14 flex items-center justify-center bg-green-100 text-green-600 rounded-full">
                <CheckCircle className="w-6 h-6" />
              </div>
            </div>
            <h4 className="text-lg font-bold text-gray-800 dark:text-white mt-4">
              Subcategory Updated Successfully
            </h4>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              The subcategory has been updated successfully.
            </p>
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => setSuccessModalOpen(false)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                OK
              </button>
            </div>
          </Modal>
        </div>
      )}
    </div>
  );
};

export default SubCategoryEdit;